import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog }from '@angular/material/legacy-dialog';
import { ContextChange, WidgetStyle } from '@sdbn/appclient';
import { ConfirmDialogComponent, ConfirmDialogData } from 'src/app/shared/confirm-dialog/confirm.dialog.component';
import { TranslationService } from '@sdbn/common';
import { ResponseMessage } from '@sdbn/appclient';
import { ResponseStatus } from '@sdbn/appclient';
import { AutomationRule, AutomationRuleDetails, AutomationRuleIndex, AutomationRuleStation, AutomationRuleType, Station } from '../automation-rules.model';
import { AutomationRulesService } from '../automation-rules.service';
import { MasterDetailListEntry } from '@sdbn/common';
import { AbstractWidgetComponent } from '@sdbn/appclient';
import { Subscription } from 'rxjs';
import { UntypedFormControl as FormControl, UntypedFormGroup as FormGroup, Validators } from '@angular/forms';
import { Row } from '@sdbn/appclient';
import { TableSelectionDialogComponent, TableSelectionDialogData } from 'src/tmc-common/components/table-selection-dialog/table-selection-dialog.component';
import { TmcTableModel } from '@sdbn/appclient';
import { DialogLayout } from 'src/app/shared/dialog-layout/dialog-layout';
import { SelectionType } from '@sdbn/appclient';
import { ColumnType } from '@sdbn/appclient';

@Component({
  selector: 'app-automation-rules',
  templateUrl: './automation-rules.component.html',
  styleUrls: ['./automation-rules.component.scss']
})
export class AutomationRulesComponent extends AbstractWidgetComponent implements OnInit {

  automationRuleType = AutomationRuleType;

  showStationsError = false;
  automationRuleTypeOptions = new Array<AutomationRuleType>();
  newId = -1;
  detailsForm: FormGroup;
  formSubscription: Subscription;

  rules: AutomationRule[] = [];

  selectedRule: AutomationRule;
  ruleDetails: AutomationRuleDetails = new AutomationRuleDetails();

  responseMessages: ResponseMessage[];
  isLoading = false;

  get cancelEnabled() {
    return !this.isLoading && this.detailsForm != null && this.detailsForm.dirty;
  }

  get saveEnabled() {
    return !this.isLoading && this.detailsForm != null && this.detailsForm.dirty && this.detailsForm.valid && !this.showStationsError;
  }

  constructor(
    public translationService: TranslationService,
    private rulesService: AutomationRulesService,
    private automationRuleService: AutomationRulesService,
    private dialog: MatDialog) {
    super();
    this.ruleDetails = new AutomationRuleDetails();
    this.ruleDetails.index = this.rules.length;

    for (const key of Object.keys(AutomationRuleType)) {
      this.automationRuleTypeOptions.push(AutomationRuleType[key]);
    }

    this.detailsForm = new FormGroup({
      type: new FormControl('', [Validators.required]),
      name: new FormControl('', [Validators.required]),
      index: new FormControl('', [Validators.required, Validators.min(0)]),
      numberPlate: new FormControl('', []),
      carrier: new FormControl('', []),
      orderReference: new FormControl('', []),
      onlyPlanned: new FormControl('', [])
    });
    this.resetForm();
    this.subscribeToForm();
  }

  ngOnInit() {
    this.load();
  }

  setSelected(rule: AutomationRule) {

    if (this.selectedRule != null && this.selectedRule.id === rule.id) {
      return;
    }
    const oldSelected = this.selectedRule;

    if (this.ruleDetails != null && this.detailsForm.dirty) {
      const dialogData: ConfirmDialogData = {
        titleKey: 'tmc-yard.widgets.automation-rules.discard.confirm-dialog.title',
        messageKey: 'tmc-yard.widgets.automation-rules.discard.confirm-dialog.message',
        confirm: () => {
          this.setSelectedWithoutCheck(rule);
        },
        cancel: () => {
          this.selectedRule = oldSelected;
        }
      };
      this.dialog.open(ConfirmDialogComponent, {
        data: dialogData
      });
    } else {
      this.setSelectedWithoutCheck(rule);
    }
  }

  setSelectedWithoutCheck(rule: AutomationRule) {
    if (rule != null) {
      this.isLoading = true;
      this.responseMessages = [];
      this.rulesService.getDetails(rule.id).subscribe(details => {
        this.selectedRule = rule;
        this.ruleDetails = new AutomationRuleDetails(details);
        this.resetForm();
        this.isLoading = false;
      }, error => {
        this.isLoading = false;
        console.log(error);
      });
    }
  }

  isSelected(details: AutomationRuleDetails) {
    return this.selectedRule != null && this.selectedRule.id === details.id;
  }

  delete(smartphone: AutomationRule) {

    this.responseMessages = [];

    const dialogData: ConfirmDialogData = {
      titleKey: 'tmc-yard.widgets.automation-rules.delete.confirm-dialog.title',
      messageKey: 'tmc-yard.widgets.automation-rules.delete.confirm-dialog.message',
      confirm: () => {
        this.isLoading = true;
        this.rulesService.deleteRule(smartphone.id).subscribe(response => {
          if (response.status === ResponseStatus.SUCCESS) {
            this.clearAndLoad();
          } else {
            this.responseMessages = response.responseMessages;
          }
          this.isLoading = false;
        }, error => {
          console.log(error);
          this.isLoading = false;
        });
      }
    };

    this.dialog.open(ConfirmDialogComponent, {
      data: dialogData
    });
  }

  load() {
    this.isLoading = true;
    this.responseMessages = [];
    this.rulesService.getRules().subscribe(rules => {

      this.rules = [];
      for (const rule of rules) {
        this.rules.push(new AutomationRule(rule));
      }
      if (this.rules.length > 0) {
        if (this.selectedRule == null) {
          this.setSelectedWithoutCheck(this.rules[0]);
        } else {
          this.setSelectedWithoutCheck(this.rules.find(s => s.id === this.selectedRule.id));
        }
      }

      this.isLoading = false;
    }, error => {
      console.error(error);
      this.isLoading = false;
    });
  }

  addNew() {
    if (this.ruleDetails != null && this.detailsForm.dirty) {
      const dialogData: ConfirmDialogData = {
        titleKey: 'tmc-yard.widgets.automation-rules.discard.confirm-dialog.title',
        messageKey: 'tmc-yard.widgets.automation-rules.discard.confirm-dialog.message',
        confirm: () => {
          this.clear();
        }
      };
      this.dialog.open(ConfirmDialogComponent, {
        data: dialogData
      });
    } else {
      this.clear();
    }
  }

  cancel() {
    if (this.ruleDetails != null && this.detailsForm.dirty) {
      const dialogData: ConfirmDialogData = {
        titleKey: 'tmc-yard.widgets.automation-rules.discard.confirm-dialog.title',
        messageKey: 'tmc-yard.widgets.automation-rules.discard.confirm-dialog.message',
        confirm: () => {
          this.discardChanges();
        }
      };
      this.dialog.open(ConfirmDialogComponent, {
        data: dialogData
      });
    } else {
      this.discardChanges();
    }
  }

  discardChanges() {
    this.responseMessages = [];
    if (this.selectedRule != null && this.detailsForm.dirty) {
      this.setSelectedWithoutCheck(this.selectedRule);
    } else {
      this.ruleDetails = new AutomationRuleDetails();
      this.ruleDetails.index = this.rules.length;
      this.resetForm();
      this.selectedRule = null;
    }
  }

  clear() {
    this.responseMessages = [];
    this.selectedRule = null;
    this.ruleDetails = new AutomationRuleDetails();
    this.ruleDetails.type = AutomationRuleType.ASSIGNMENT;
    this.ruleDetails.index = this.rules.length;
    this.resetForm();
  }

  clearAndLoad() {
    this.clear();
    this.load();
  }

  public save() {
    this.isLoading = true;
    this.responseMessages = [];
    this.rulesService.saveDetails(this.ruleDetails).subscribe(response => {
      this.isLoading = false;
      if (response.status === ResponseStatus.SUCCESS) {
        this.load();
      } else {
        this.responseMessages = response.responseMessages;
      }
    }, error => {
      console.log(error);
      this.isLoading = false;
    });

  }

  public drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.rules, event.previousIndex, event.currentIndex);
    this.updateRulesIndex();
    this.saveRulesIndex();
  }

  private updateRulesIndex() {
    let index = 0;
    for (const rule of this.rules) {
      rule.index = index++;
    }
  }

  private saveRulesIndex() {
    const rulesIndices = this.rules.map(r => new AutomationRuleIndex(r.id, r.index));
    this.rulesService.saveRulesIndex(rulesIndices).subscribe(response => {
      this.isLoading = false;
      if (response.status === ResponseStatus.SUCCESS) {
        this.load();
      } else {
        this.responseMessages = response.responseMessages;
      }
    }, error => {
      console.log(error);
      this.isLoading = false;
    });
  }

  onContextChange(contextChange: ContextChange) {
  }

  onSetElementAndContext() {
  }

  onSelectionChanged(entry: MasterDetailListEntry) {
    this.setSelected(entry as AutomationRule);
  }

  onAddEntry(parentPath: Array<MasterDetailListEntry>) {
    this.addNew();
  }

  onDeleteEntry(entry: MasterDetailListEntry) {
    this.delete(entry as AutomationRule);
  }

  public removeStationFromStationAssignmentRule(station: AutomationRuleStation) {
    if (station.stationId <= 0) {
      const index = this.ruleDetails.stations.indexOf(station);
      this.ruleDetails.stations.splice(index, 1);
    } else {
      station.deleted = true;
    }
    this.updateAssignmentRuleStationIndex();
    this.detailsForm.markAsDirty();
    this.updateValidation();
  }

  public addStationToStationAssignmentRule() {

    this.automationRuleService.getStations().subscribe(
      stationsResult => {

        const stations = new Array<Station>();
        for (const station of stationsResult) {
          stations.push(new Station(station));
        }

        const assignedStationIds = this.ruleDetails.stations.filter(station => !station.deleted).map(station => station.stationId);
        const assignableStations = stations.filter(station => assignedStationIds.indexOf(station.id) === -1);

        const tableSelectionDialogData: TableSelectionDialogData = {
          title: this.translationService.translate('tmc-yard.widgets.automation-rules.stations.dialog.title'),
          tableModel: this.createTableModelForStationSelectionDialog(assignableStations),
          confirmCallback: (tableModel: TmcTableModel) => {
            const selectedStations = this.convertRowsToAutomationRuleStations(tableModel.selectedRows);
            this.ruleDetails.stations.push(...selectedStations);
            this.updateAssignmentRuleStationIndex();
            this.detailsForm.markAsDirty();
            this.updateValidation();
          }
        };

        this.dialog.open(TableSelectionDialogComponent, {
          data: tableSelectionDialogData,
          panelClass: DialogLayout.panelClass
        });
      }
    );
  }

  private convertRowsToAutomationRuleStations(selectedRows: Row[]): AutomationRuleStation[] {
    return selectedRows.map(row => {
      const station: AutomationRuleStation = new AutomationRuleStation();
      station.id = this.newId--;
      station.stationId = row.values.id;
      station.name = row.values.name;
      return station;
    });
  }

  private createTableModelForStationSelectionDialog(assignableStations: Station[]): TmcTableModel {
    const tableModel = new TmcTableModel();
    tableModel.selectionType = SelectionType.MULTIPLE;
    tableModel.sortable = true;
    tableModel.filterable = true;
    tableModel.sortColumn = 'name';
    tableModel.sortDirection = 'asc';
    const prefix = 'tmc-yard.widgets.automation-rules.stations.dialog.table.column.';
    tableModel.columnDefinitions = [
      { id: 'name', name: 'name', visible: true, type: ColumnType.STRING, translationKey: prefix + 'name' },
      { id: 'description', name: 'description', visible: true, type: ColumnType.STRING, translationKey: prefix + 'description' },
      { id: 'stationtype', name: 'stationtype', visible: true, type: ColumnType.STRING, translationKey: prefix + 'stationtype' }
    ];
    tableModel.setRows(this.convertStationsToRows(assignableStations));
    return tableModel;
  }

  private convertStationsToRows(assignableStations: Station[]): Row[] {
    const rows: Row[] = [];
    for (const station of assignableStations) {
      const row: Row = new Row();
      row.id = station.id + '';
      row.values = {
        id: station.id,
        name: station.name,
        description: station.description,
        stationtype: this.translationService.translate('tmc.stationgroup.stationset.stationType.' + station.stationType),
      };

      rows.push(row);
    }
    return rows;
  }

  private updateAssignmentRuleStationIndex() {

    const stations = this.getStationsOfRule();

    for (let i = 0; i < stations.length; i++) {
      stations[i].index = i;
    }
  }

  public getStationsOfRule() {
    return this.ruleDetails.stations.filter(s => !s.deleted);
  }

  private resetForm() {

    this.unsubscribeFromForm();

    try {

      const values: any = {};
      values.type = this.ruleDetails.type;
      values.index = this.ruleDetails.index;
      values.name = this.ruleDetails.name;
      values.numberPlate = this.ruleDetails.numberPlate;
      values.carrier = this.ruleDetails.carrier;
      values.orderReference = this.ruleDetails.orderReference;
      values.onlyPlanned = this.ruleDetails.onlyPlanned;

      this.detailsForm.reset(values);
      this.updateValidation();
    } finally {
      this.subscribeToForm();
    }
  }

  private subscribeToForm() {
    this.formSubscription = this.detailsForm.valueChanges.subscribe(
      () => {
        if (this.detailsForm.dirty) {
          const values = this.detailsForm.getRawValue();
          this.ruleDetails.type = values.type;
          this.ruleDetails.index = values.index;
          this.ruleDetails.name = values.name;
          this.ruleDetails.numberPlate = values.numberPlate;
          this.ruleDetails.carrier = values.carrier;
          this.ruleDetails.orderReference = values.orderReference;
          this.ruleDetails.onlyPlanned = values.onlyPlanned;
          this.updateValidation();
        }
      }
    );
  }

  private unsubscribeFromForm() {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
  }

  public dropAssignmentRuleStation(event: CdkDragDrop<string[]>) {
    this.detailsForm.markAsDirty();

    moveItemInArray(this.ruleDetails.stations, event.previousIndex, event.currentIndex);
    this.updateAssignmentRuleStationIndex();
    this.detailsForm.markAsDirty();
  }

  private updateValidation() {
    this.showStationsError = this.ruleDetails.stations.filter(s => !s.deleted).length === 0;
  }

  getStyle(): WidgetStyle {
    return WidgetStyle.transparent;
  }

}
